import {
  Tabs,
  TabsContent,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { pageToUrl } from '@client';
import { WizardAppConfigFeatureFlags } from '@/pages/WizardAppConfigFeatureFlags';
import {
  FeatureFlagsForm,
  ReportingAttributesForm,
  AccountIdentifiersForm,
  DeleteAccountForm,
  PreRegisteredFeatureFlagsForm,
  PreRegisteredTeamRelationshipsForm,
} from '@/features/wizard-advisor-admin';

const DEFAULT_TAB = 'account-identifiers';
const APP_CONFIG_OPTION = 'app-config-feature-flags';

const SECTION_OPTIONS: { value: string; label: string; danger?: boolean }[] = [
  { value: 'account-identifiers', label: 'Account Identifiers' },
  { value: 'feature-flags', label: 'Feature Flags' },
  { value: 'pre-registered-flags', label: 'Pre-Registered Flags' },
  { value: 'pre-registered-teams', label: 'Pre-Registered Teams' },
  { value: 'reporting-attributes', label: 'Reporting Attributes' },
  { value: 'delete-account', label: 'Delete Account', danger: true },
];

export function WizardAdvisorAdminPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedSection = searchParams.get('tab') ?? DEFAULT_TAB;

  const onSectionChange = (value: string) => {
    if (value === APP_CONFIG_OPTION) {
      navigate(pageToUrl(WizardAppConfigFeatureFlags));
      return;
    }
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Advisor Admin Portal"
        description="Manage advisor feature flags and reporting attributes"
      />

      <Tabs value={selectedSection}>
        <div className="mb-6 mt-4">
          <Select
            value={selectedSection}
            onValueChange={onSectionChange}
          >
            <SelectTrigger className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECTION_OPTIONS.map(opt => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className={opt.danger ? 'text-red-600 focus:text-red-700' : undefined}
                >
                  {opt.label}
                </SelectItem>
              ))}
              <SelectSeparator />
              <SelectItem value={APP_CONFIG_OPTION}>Manage AppConfig Feature Flags</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="feature-flags">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Manage Feature Flags</CardTitle>
              <CardDescription className="text-gray-500">
                Search for an advisor by ID or email, then manage their feature flags.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureFlagsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pre-registered-flags">
          <Card className="max-w-2xl border shadow-sm">
            <CardHeader>
              <CardTitle>Pre-Registered Feature Flags</CardTitle>
              <CardDescription className="text-muted-foreground">
                Pre-register emails so feature flags are automatically applied when the advisor
                signs up.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PreRegisteredFeatureFlagsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pre-registered-teams">
          <PreRegisteredTeamRelationshipsForm />
        </TabsContent>

        <TabsContent value="reporting-attributes">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Manage Advisor Attributes</CardTitle>
              <CardDescription className="text-gray-500">
                Add, update, or delete advisor reporting attributes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ReportingAttributesForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account-identifiers">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Update Account Identifiers</CardTitle>
              <CardDescription className="text-gray-500">
                Update an advisor's email address or phone number.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AccountIdentifiersForm />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="delete-account">
          <Card className="max-w-2xl border border-red-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-red-700">Delete Advisor Account</CardTitle>
              <CardDescription className="text-red-500">
                Permanently delete an advisor account and all associated data. This action cannot be
                undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DeleteAccountForm />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
